import { useEffect, useState } from "react";
import { Loader2, RefreshCw } from "lucide-react";
import { adminApi } from "@/lib/admin";

const RATINGS = [
  { id: "more_hbm_like", label: "More HBM-like" },
  { id: "best_answer", label: "Best answer" },
  { id: "too_generic", label: "Too generic" },
  { id: "too_cynical", label: "Too cynical" },
  { id: "over_inferred", label: "Over-inferred motive" },
];

const BRANCH_LABEL = {
  gemini_hbm: "Gemini · HBM",
  claude_hbm: "Claude · HBM",
  baseline: "Generic baseline",
};

export default function HBMEvalSection() {
  const [query, setQuery] = useState("");
  const [running, setRunning] = useState(false);
  const [run, setRun] = useState(null); // {run_id, query, results}
  const [error, setError] = useState("");
  const [voted, setVoted] = useState({}); // {branch: ratingId}
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  const loadSummary = async () => {
    setSummaryLoading(true);
    try {
      setSummary(await adminApi.hbmEvalSummary());
    } catch {
      // not fatal
    } finally {
      setSummaryLoading(false);
    }
  };

  useEffect(() => {
    loadSummary();
  }, []);

  const onRun = async (e) => {
    e.preventDefault();
    if (!query.trim() || running) return;
    setRunning(true);
    setError("");
    setRun(null);
    setVoted({});
    try {
      const r = await adminApi.hbmEval(query.trim());
      setRun(r);
    } catch (err) {
      setError(err.message || "Eval failed.");
    } finally {
      setRunning(false);
    }
  };

  const vote = async (branch, rating) => {
    if (!run) return;
    if (voted[branch] === rating) return; // already voted this
    try {
      await adminApi.hbmEvalVote(run.run_id, branch, rating, "");
      setVoted({ ...voted, [branch]: rating });
      loadSummary(); // refresh aggregate
    } catch (err) {
      alert(err.message || "Vote failed.");
    }
  };

  return (
    <div className="space-y-6" data-testid="hbm-eval-content">
      {/* Form */}
      <form onSubmit={onRun} className="space-y-3">
        <textarea
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="e.g. My manager throws me under the bus in public but is friendly one-on-one. What is actually going on?"
          rows={3}
          data-testid="hbm-eval-input"
          className="w-full bg-transparent border border-[var(--border)] focus:border-[var(--accent)] outline-none p-3 font-sans text-sm resize-none transition-colors"
        />
        <button
          type="submit"
          disabled={running || !query.trim()}
          data-testid="hbm-eval-run"
          className="px-5 py-2 font-mono-ui text-[10px] tracking-[0.28em] uppercase border border-[var(--accent)] text-[var(--accent)] hover:bg-[var(--accent)] hover:text-[var(--bg)] disabled:opacity-40 transition-colors inline-flex items-center gap-2"
        >
          {running && <Loader2 size={12} className="animate-spin" />}
          {running ? "Running 3 branches…" : "Run on all 3 branches"}
        </button>
      </form>
      {error && (
        <p data-testid="hbm-eval-error" className="font-sans text-sm text-red-400 border-l-2 border-red-400 pl-3">
          {error}
        </p>
      )}

      {/* Three side-by-side answers */}
      {run && (
        <div className="grid md:grid-cols-3 gap-4" data-testid="hbm-eval-results">
          {run.results.map((r) => {
            const branch = r.branch;
            const isVoted = voted[branch];
            return (
              <div
                key={branch}
                data-testid={`hbm-eval-card-${branch}`}
                className="border border-[var(--border)] p-4 flex flex-col"
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="font-mono-ui text-[10px] tracking-[0.24em] uppercase text-[var(--accent)]">
                    {BRANCH_LABEL[branch]}
                  </span>
                  <span className="font-mono-ui text-[9px] tracking-[0.18em] uppercase text-[var(--text-dim)]">
                    {r.engine} · {r.latency_ms}ms
                  </span>
                </div>
                <div className="flex-1 mb-4">
                  {r.error ? (
                    <p className="font-mono-ui text-xs text-red-400 whitespace-pre-wrap">{r.error}</p>
                  ) : (
                    <p className="font-sans text-[13px] leading-relaxed whitespace-pre-wrap text-[var(--text)]">
                      {r.content}
                    </p>
                  )}
                </div>
                {/* Rating buttons */}
                <div className="flex flex-wrap gap-1.5" data-testid={`hbm-eval-vote-row-${branch}`}>
                  {RATINGS.map((rt) => (
                    <button
                      key={rt.id}
                      type="button"
                      onClick={() => vote(branch, rt.id)}
                      disabled={!!voted[branch] && voted[branch] !== rt.id}
                      data-testid={`hbm-eval-vote-${branch}-${rt.id}`}
                      className={`px-2.5 py-1 font-mono-ui text-[9px] tracking-[0.22em] uppercase border transition-colors ${
                        isVoted === rt.id
                          ? "border-[var(--accent)] text-[var(--accent)] bg-[var(--accent)]/10"
                          : "border-[var(--border)] text-[var(--text-muted)] hover:border-[var(--accent)] hover:text-[var(--accent)] disabled:opacity-40"
                      }`}
                    >
                      {rt.label}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Aggregate summary */}
      <div className="border-t border-[var(--border)] pt-5" data-testid="hbm-eval-summary">
        <div className="flex items-center justify-between mb-3">
          <span className="font-mono-ui text-[10px] tracking-[0.28em] uppercase text-[var(--text-dim)]">
            All-time aggregate
          </span>
          <button
            onClick={loadSummary}
            data-testid="hbm-eval-summary-refresh"
            className="font-mono-ui text-[10px] tracking-[0.24em] uppercase text-[var(--text-muted)] hover:text-[var(--accent)] inline-flex items-center gap-1.5"
          >
            <RefreshCw size={11} className={summaryLoading ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>
        {!summary || summary.total_votes === 0 ? (
          <p
            data-testid="hbm-eval-summary-empty"
            className="font-ui text-sm text-[var(--text-muted)]"
          >
            No votes yet. Run a few evals and rate the outputs.
          </p>
        ) : (
          <div className="space-y-4">
            <div className="font-mono-ui text-[10px] tracking-[0.22em] uppercase text-[var(--text-dim)]">
              {summary.total_runs} runs · {summary.total_votes} votes
              {summary.best_performing_engine && (
                <span className="ml-3 text-[var(--accent)]">
                  Best-performing: {BRANCH_LABEL[summary.best_performing_engine] || summary.best_performing_engine}
                </span>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="hbm-eval-summary-branches">
              {Object.entries(summary.per_branch).map(([branch, s]) => (
                <div key={branch} data-testid={`hbm-eval-summary-${branch}`} className="border border-[var(--border)] p-3 space-y-1">
                  <div className="font-mono-ui text-[10px] tracking-[0.22em] uppercase text-[var(--accent)] mb-1.5">
                    {BRANCH_LABEL[branch]}
                  </div>
                  <RowLine label="Best-answer rate" value={`${s.best_answer_pct}%`} accent={s.best_answer_pct >= 50} />
                  <RowLine label="HBM-like" value={`${s.more_hbm_like_pct}%`} />
                  <RowLine label="Too generic" value={s.too_generic} warn={s.too_generic > 0} />
                  <RowLine label="Too cynical" value={s.too_cynical} warn={s.too_cynical > 0} />
                  <RowLine label="Over-inferred" value={s.over_inferred} warn={s.over_inferred > 0} />
                  <RowLine label="Votes" value={s.votes_total} />
                </div>
              ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="hbm-eval-summary-failure">
              <FailureTile label="Generic failure rate" value={`${summary.generic_failure_rate_pct}%`} tone={summary.generic_failure_rate_pct > 20 ? "warn" : "muted"} testid="hbm-fail-generic" />
              <FailureTile label="Over-inference rate" value={`${summary.over_inference_rate_pct}%`} tone={summary.over_inference_rate_pct > 20 ? "warn" : "muted"} testid="hbm-fail-overinfer" />
              <FailureTile label="Too-cynical rate" value={`${summary.too_cynical_rate_pct}%`} tone={summary.too_cynical_rate_pct > 20 ? "warn" : "muted"} testid="hbm-fail-cynical" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function RowLine({ label, value, accent = false, warn = false }) {
  return (
    <div className="flex items-center justify-between text-[10px] font-mono-ui tracking-[0.18em] uppercase">
      <span className="text-[var(--text-muted)]">{label}</span>
      <span
        className={
          accent
            ? "text-[var(--accent)]"
            : warn
            ? "text-yellow-400"
            : "text-[var(--text)]"
        }
      >
        {value}
      </span>
    </div>
  );
}

function FailureTile({ label, value, tone, testid }) {
  const accentClass = {
    warn: "text-yellow-400",
    muted: "text-[var(--text)]",
  }[tone];
  return (
    <div data-testid={testid} className="border border-[var(--border)] p-3">
      <div className="font-mono-ui text-[9px] tracking-[0.22em] uppercase text-[var(--text-dim)] mb-1">
        {label}
      </div>
      <div className={`font-prose text-2xl leading-none ${accentClass}`}>{value}</div>
    </div>
  );
}
